import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FilterDef } from '../core/schema/types';
import { formatFilterDateDisplay, resolveDateRangeEndField } from '../core/engine/filters';
import { consumeUrlFilters } from '../utils/urlFilters';

function pickKnownFilterValues(
  source: Record<string, unknown>,
  filterDefs: readonly FilterDef[],
): Record<string, unknown> {
  const knownNames = new Set<string>();
  for (const def of filterDefs) {
    knownNames.add(def.name);
    const endField = resolveDateRangeEndField(def);
    if (endField) knownNames.add(endField);
  }

  const picked: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    if (knownNames.has(key)) {
      picked[key] = value;
    }
  }
  return picked;
}

interface UseTableFiltersParams {
  filterDefs: readonly FilterDef[];
  tableStateFilters: Record<string, unknown>;
  stateActions: {
    setFilter: (key: string, value: unknown) => void;
    setFilters: (filters: Record<string, unknown>) => void;
    setSearch: (text: string) => void;
  };
  onFilterChange?: (filter: Record<string, unknown>) => void;
  onSearchChange?: (text: string) => void;
  setTableInfo?: (info: { skipCount: number; maxResultCount: number; sorting: string }) => void;
  tableInfo?: { skipCount: number; maxResultCount: number; sorting: string };
  initialFiltersProp?: Record<string, unknown>;
  initialSearchText?: string;
  searchDebounce: number;
}

interface FilterChip {
  fieldName: string;
  label: string;
  displayValue: string;
}

interface UseTableFiltersReturn {
  searchInput: string;
  handleSearchChange: (text: string) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  pendingFilter: Record<string, unknown>;
  handleFilterChange: (name: string, value: unknown) => void;
  handleApplyFilter: () => void;
  handleResetFilter: () => void;
  filterChips: FilterChip[];
  setFilterChips: React.Dispatch<React.SetStateAction<FilterChip[]>>;
  handleAsyncOptionsLoaded: (name: string, options: Array<{ label: string; value: string | number }>) => void;
  filterDefsRef: React.MutableRefObject<readonly FilterDef[]>;
}

export function useTableFilters({
  filterDefs,
  tableStateFilters,
  stateActions,
  onFilterChange,
  onSearchChange,
  setTableInfo,
  tableInfo,
  initialFiltersProp,
  initialSearchText,
  searchDebounce,
}: UseTableFiltersParams): UseTableFiltersReturn {
  // Deep-link / AI chatbot filter application (runs once on mount)
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    deepLinkAppliedRef.current = true;

    const urlFilters = pickKnownFilterValues(consumeUrlFilters(), filterDefs);
    const initialFilters = pickKnownFilterValues(initialFiltersProp ?? {}, filterDefs);
    const merged: Record<string, unknown> = {
      ...urlFilters,
      ...initialFilters,
    };
    if (Object.keys(merged).length > 0) {
      Object.entries(merged).forEach(([key, value]) => {
        stateActions.setFilter(key, value);
      });
    }
    if (initialSearchText) {
      setSearchInput(initialSearchText);
      stateActions.setSearch(initialSearchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  const [searchInput, setSearchInput] = useState(initialSearchText ?? '');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchInput(text);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        stateActions.setSearch(text);
        onSearchChange?.(text);
        if (setTableInfo && tableInfo) {
          setTableInfo({ ...tableInfo, skipCount: 0 });
        }
      }, searchDebounce);
    },
    [searchDebounce, stateActions, onSearchChange, setTableInfo, tableInfo]
  );

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // Filter panel open/close
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Pending filter state
  const [pendingFilter, setPendingFilter] = useState<Record<string, unknown>>(() => ({
    ...tableStateFilters,
  }));

  useEffect(() => {
    if (!isFilterOpen) {
      setPendingFilter({ ...tableStateFilters });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterOpen, tableStateFilters]);

  // Async filter options cache
  const asyncOptionsCacheRef = useRef<
    Map<string, Array<{ label: string; value: string | number }>>
  >(new Map());

  const handleAsyncOptionsLoaded = useCallback(
    (name: string, options: Array<{ label: string; value: string | number }>) => {
      asyncOptionsCacheRef.current.set(name, options);
    },
    []
  );

  // Filter chips
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);

  const filterDefsRef = useRef<readonly FilterDef[]>(filterDefs);
  filterDefsRef.current = filterDefs;

  const handleFilterChange = useCallback((name: string, value: unknown) => {
    setPendingFilter((prev) => {
      const next: Record<string, unknown> = { ...prev, [name]: value };
      const def = filterDefsRef.current.find((d) => d.name === name);
      const clears = def?.clears;
      if (Array.isArray(clears)) {
        for (const c of clears) {
          delete next[c];
        }
      }
      return next;
    });
  }, []);

  const resolveDisplayValue = useCallback(
    (fieldName: string, value: unknown, filterDef: FilterDef): string => {
      if (value === undefined || value === null || value === '') return '';

      if (filterDef.type === 'select' || filterDef.type === 'asyncSelect') {
        const opts: Array<{ label: string; value: string | number }> =
          (filterDef.options as Array<{ label: string; value: string | number }> | undefined) ??
          asyncOptionsCacheRef.current.get(fieldName) ??
          [];

        const values = Array.isArray(value) ? value : [value];
        const labels = values
          .map((v) => opts.find((o) => String(o.value) === String(v))?.label ?? String(v))
          .filter(Boolean);
        return labels.join(', ');
      }

      if (filterDef.type === 'date' || filterDef.type === 'dateRange') {
        return formatFilterDateDisplay(value);
      }

      return String(value);
    },
    []
  );

  const handleApplyFilter = useCallback(() => {
    const nextFilters: Record<string, unknown> = {};
    Object.entries(pendingFilter).forEach(([name, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        nextFilters[name] = value;
      }
    });

    stateActions.setFilters(nextFilters);

    if (onFilterChange) {
      onFilterChange(nextFilters);
    }
    if (setTableInfo && tableInfo) {
      setTableInfo({ ...tableInfo, ...nextFilters, skipCount: 0 });
    }

    const chips: FilterChip[] = [];
    Object.entries(nextFilters).forEach(([name, value]) => {
      if (Array.isArray(value) && value.length === 0) return;
      let def = filterDefsRef.current.find((f) => f.name === name);
      if (!def) {
        def = filterDefsRef.current.find(
          (f) => f.type === 'dateRange' && resolveDateRangeEndField(f) === name
        );
      }
      if (!def) return;
      const displayValue = resolveDisplayValue(name, value, def);
      if (displayValue) {
        const endKey = resolveDateRangeEndField(def);
        const label =
          def.type === 'dateRange' && name === endKey
            ? `${def.placeholder} (to)`
            : def.placeholder ?? name;
        chips.push({ fieldName: name, label, displayValue });
      }
    });
    setFilterChips(chips);
    setIsFilterOpen(false);
  }, [pendingFilter, stateActions, onFilterChange, setTableInfo, tableInfo, resolveDisplayValue]);

  const handleResetFilter = useCallback(() => {
    setPendingFilter({});
    stateActions.setFilters({});
    setFilterChips([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setSearchInput('');
    stateActions.setSearch('');
    onSearchChange?.('');
    if (onFilterChange) onFilterChange({});
    if (setTableInfo && tableInfo) {
      setTableInfo({ ...tableInfo, skipCount: 0 });
    }
    setIsFilterOpen(false);
  }, [stateActions, onFilterChange, onSearchChange, setTableInfo, tableInfo]);

  return {
    searchInput,
    handleSearchChange,
    isFilterOpen,
    setIsFilterOpen,
    pendingFilter,
    handleFilterChange,
    handleApplyFilter,
    handleResetFilter,
    filterChips,
    setFilterChips,
    handleAsyncOptionsLoaded,
    filterDefsRef,
  };
}
